/**
 * #731 — the order card's row BOUNDARY.
 *
 * Scope item 2 asks for extracted row boundaries with "a complete, explicit
 * dependency signature and stable callbacks/props"; item 5 says `memo()` is the
 * last step, "not as the architecture". This is that boundary.
 *
 * ### Why a component and not a cache
 *
 * The first attempt kept the JSX in the parent and memoised each row's handlers
 * in a keyed cache held in a ref. It produced the right numbers and was wrong:
 * reading and mutating a ref DURING RENDER is not sound under concurrent
 * rendering — a render React discards would leave the cache half-rotated — and
 * `react-hooks/refs` rejects it, which the repo's lint budget treats as a hard
 * gate. A component gets the same result with none of that: each row instance
 * owns its own `useCallback`s, so React does the per-row memoisation natively.
 *
 * ### The dependency signature is the safety contract
 *
 * Each handler lists every value it closes over. A captured value missing from
 * its list would let a cancel or an edit run against a stale figure — the one
 * failure worse than the slowness this ticket fixes. The props are also the
 * signature `memo` compares: `vm` arrives already identity-stabilised (see
 * `stableVm.ts`), so an unchanged row compares equal and is not invoked.
 *
 * `highlighted` is deliberately a plain boolean rather than a derived object:
 * the same-game map is rebuilt every publication by design, so passing it (or
 * anything derived from its identity) would rebuild every row for a highlight
 * that did not move. A boolean compares by value.
 */
import { memo, useCallback, type Dispatch, type SetStateAction } from 'react';
import { OrderCard } from '../../components/cards/OrderCard';
import type { OrderCardVM } from '../../components/cards/orderCardModel';
import type { EventStateCertainty } from '../../lib/sport/eventStateCertainty';
import type { EditOrderData, SameGameRowRef } from './index';

export interface OrderCardRowProps {
  vm: OrderCardVM;
  eventStateCertainty: EventStateCertainty | undefined;
  highlighted: boolean;
  queueCriticalFlash: boolean;
  queueWarningFlash: boolean;
  fellBehindFlash: boolean;
  velocitySpikeSeverity: React.ComponentProps<typeof OrderCard>['velocitySpikeSeverity'];
  velocityMuted: boolean;
  divergence: React.ComponentProps<typeof OrderCard>['divergence'];
  divergenceFlash: boolean;
  upOdds: React.ComponentProps<typeof OrderCard>['upOdds'];
  detailHref: string | null;
  canceling: boolean;
  pinned: boolean;
  /** The row's own label — derived once by the parent and passed, so the two
   *  handlers that show it to the trader cannot disagree with the header. */
  label: string;
  /** Poly navigate fallback for `onOpen`; null for Kalshi rows. */
  href: string | null;
  /* Stable parent dispatchers. */
  setHoveredRow: Dispatch<SetStateAction<SameGameRowRef | null>>;
  orderCardOpen: (
    vm: Pick<OrderCardVM, 'venue' | 'ticker' | 'side'>,
    href: string | null,
  ) => () => void;
  requestCancelOrder: (target: {
    venue: OrderCardVM['venue'];
    orderId: string;
    label: string;
  }) => void;
  setEditOrder: Dispatch<SetStateAction<EditOrderData | null>>;
  togglePin: (entry: { key: string; kind: 'order'; label: string }) => void;
  toggleVelocityMute: (key: string) => void;
}

function OrderCardRowInner({
  vm,
  label,
  href,
  setHoveredRow,
  orderCardOpen,
  requestCancelOrder,
  setEditOrder,
  togglePin,
  toggleVelocityMute,
  ...cardProps
}: OrderCardRowProps) {
  const onMouseEnter = useCallback(
    () => setHoveredRow({ venue: vm.venue, ticker: vm.ticker, key: vm.key }),
    [setHoveredRow, vm.venue, vm.ticker, vm.key],
  );
  const onMouseLeave = useCallback(() => setHoveredRow(null), [setHoveredRow]);
  const onToggleVelocityMute = useCallback(
    () => toggleVelocityMute(vm.key),
    [toggleVelocityMute, vm.key],
  );
  const onOpen = useCallback(() => orderCardOpen(vm, href)(), [orderCardOpen, vm, href]);
  const onCancel = useCallback(
    () => requestCancelOrder({ venue: vm.venue, orderId: vm.cancelId, label }),
    [requestCancelOrder, vm.venue, vm.cancelId, label],
  );
  const onEdit = useCallback(() => {
    // vm.title / vm.subtitle combine into the same label the card already shows
    // in its header — keep the modal consistent with what the trader was just
    // looking at. Poly cards have side === null (no YES/NO concept on a token);
    // fall back to 'yes' as a placeholder badge.
    setEditOrder({
      venue: vm.venue,
      orderId: vm.cancelId,
      marketLabel: label,
      side: vm.side ?? 'yes',
      action: vm.isSell ? 'sell' : 'buy',
      remainingCount: vm.remainingQty,
      currentPriceCents: vm.bidCents ?? 50,
      ticker: vm.ticker,
      tickSize: vm.tickSize,
      negRisk: vm.negRisk,
    });
  }, [setEditOrder, vm, label]);
  const onTogglePin = useCallback(
    () => togglePin({ key: vm.key, kind: 'order', label }),
    [togglePin, vm.key, label],
  );

  return (
    <OrderCard
      vm={vm}
      {...cardProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onToggleVelocityMute={onToggleVelocityMute}
      onOpen={onOpen}
      onCancel={onCancel}
      onEdit={onEdit}
      onTogglePin={onTogglePin}
    />
  );
}

/** The boundary. `memo` is the LAST step here, on top of stable identities. */
export const OrderCardRow = memo(OrderCardRowInner);
